/**
 * AuthMapPanel.tsx
 * Decorative right-hand panel shared by the auth screens (login / register).
 *
 * Renders our static Bamberg streetmap SVG with a subtle "live dispatch"
 * overlay on top — flowing dashed routes, pulsing location pins, and a
 * drifting vehicle — so the panel reads as a living map rather than a flat
 * image.
 *
 * The overlay is **randomised on every mount**, so each page load / refresh
 * shows a different arrangement of routes, pins, vehicle path and timings.
 * It is purely decorative: `pointer-events-none`, `aria-hidden`,
 * theme-coloured, and fully stilled under `prefers-reduced-motion`
 * (animation defined in index.css `.auth-*`).
 *
 * Hidden below the `md` breakpoint so the form goes full-width on phones.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import bambergMap from '@/assets/images/login-bamberg-map.svg';

/** Overlay SVG user-space dimensions (viewBox). */
const W = 700;
const H = 900;

/** Random float in [min, max). */
const rand = (min: number, max: number) => min + Math.random() * (max - min);
/** Random integer in [min, max]. */
const randInt = (min: number, max: number) => Math.floor(rand(min, max + 1));

/** One animated route on the map. */
interface Route {
  d: string;
  brand: boolean;
  /** flow animation duration (s) + negative start offset (s) */
  dur: number;
  delay: number;
  start: { x: number; y: number };
  end: { x: number; y: number };
}

/** One pulsing pin. */
interface Pin {
  x: number;
  y: number;
  brand: boolean;
  dur: number;
  delay: number;
}

/** A fully-resolved random overlay scene. */
interface Scene {
  routes: Route[];
  pins: Pin[];
  vehicle: { d: string; dur: number; brand: boolean };
}

/**
 * Build a random overlay scene: 2–3 curved routes spanning the panel, pins at
 * their far ends plus a couple of free-floating ones, and a vehicle that
 * drives one of the routes. All coordinates are in the 700×900 viewBox.
 */
function buildScene(): Scene {
  const routeCount = randInt(2, 3);
  const routes: Route[] = Array.from({ length: routeCount }, () => {
    const start = { x: rand(20, 200), y: rand(60, H - 60) };
    const end = { x: rand(W - 200, W - 20), y: rand(60, H - 60) };
    const c1 = { x: rand(150, 350), y: rand(40, H - 40) };
    const c2 = { x: rand(360, 560), y: rand(40, H - 40) };
    return {
      d: `M${start.x.toFixed(0)},${start.y.toFixed(0)} C${c1.x.toFixed(0)},${c1.y.toFixed(0)} ${c2.x.toFixed(0)},${c2.y.toFixed(0)} ${end.x.toFixed(0)},${end.y.toFixed(0)}`,
      brand: Math.random() < 0.5,
      dur: rand(0.9, 1.7),
      delay: -rand(0, 1.5),
      start,
      end,
    };
  });

  // Pins: one at each route's end, plus 1–2 free-floating ones.
  const pins: Pin[] = routes.map((r) => ({
    x: r.end.x,
    y: r.end.y,
    brand: r.brand,
    dur: rand(2.4, 3.4),
    delay: -rand(0, 3),
  }));
  const extra = randInt(1, 2);
  for (let i = 0; i < extra; i++) {
    pins.push({
      x: rand(120, W - 120),
      y: rand(120, H - 120),
      brand: Math.random() < 0.5,
      dur: rand(2.4, 3.4),
      delay: -rand(0, 3),
    });
  }

  // Vehicle drives one randomly-chosen route.
  const vRoute = routes[randInt(0, routes.length - 1)];
  return {
    routes,
    pins,
    vehicle: { d: vRoute.d, dur: rand(7, 12), brand: vRoute.brand },
  };
}

/** The decorative, animated map panel for the login/register split layout. */
export default function AuthMapPanel() {
  const { t } = useTranslation();
  // Lazy initialiser → one random scene per mount, stable across re-renders
  // (so switching language doesn't re-shuffle the map mid-session).
  const [scene] = useState<Scene>(buildScene);

  return (
    <div className="relative hidden flex-1 overflow-hidden bg-secondary md:block">
      {/* Static base: our Bamberg streetmap */}
      <img
        src={bambergMap}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
      />

      {/* Soft brand wash for depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-muted/20" />

      {/* Live-activity overlay (randomised, decorative motion) */}
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        {/* Flowing routes */}
        {scene.routes.map((r, i) => (
          <path
            key={`r${i}`}
            className={`auth-route ${r.brand ? 'auth-route-brand' : 'auth-route-navy'}`}
            d={r.d}
            style={{ animationDuration: `${r.dur}s`, animationDelay: `${r.delay}s` }}
          />
        ))}

        {/* Pulsing pins (ring pulse + solid dot + light centre) */}
        {scene.pins.map((p, i) => {
          const fill = p.brand ? 'auth-fill-brand' : 'auth-fill-navy';
          return (
            <g key={`p${i}`}>
              <circle
                className={`auth-ping ${fill}`}
                cx={p.x}
                cy={p.y}
                r={8}
                style={{ animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s` }}
              />
              <circle className={fill} cx={p.x} cy={p.y} r={5} opacity={0.95} />
              <circle className="auth-fill-card" cx={p.x} cy={p.y} r={2} />
            </g>
          );
        })}

        {/* Vehicle drifting along one route */}
        <g
          className="auth-vehicle"
          style={{ offsetPath: `path("${scene.vehicle.d}")`, animationDuration: `${scene.vehicle.dur}s` }}
        >
          <circle r={5} className={scene.vehicle.brand ? 'auth-fill-brand' : 'auth-fill-navy'} />
          <circle r={2} className="auth-fill-card" />
        </g>
      </svg>

      {/* Map attribution, bottom-right */}
      <a
        href="https://www.openstreetmap.org/copyright"
        target="_blank"
        rel="noopener noreferrer"
        className="absolute bottom-4 right-5 text-[10px] tracking-[0.08em] text-muted-foreground/80 transition-opacity hover:opacity-75"
      >
        {t('login.mapAttribution')}
      </a>
    </div>
  );
}
